import React from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowRight, MapPin } from 'lucide-react';
import AppLayout from '@/components/AppLayout';
import type { Country } from '@/contexts/CountryContext';

/** Country chooser landing page shown at `/`. */
const ChooseCountry: React.FC = () => {
  const navigate = useNavigate();

  const options: { country: Country; label: string; tagline: string; path: string; image: string }[] = [
    {
      country: 'albania',
      label: 'Zera Albania',
      tagline: "Europe's Hidden Riviera",
      path: '/albania',
      image: 'https://commons.wikimedia.org/wiki/Special:FilePath/Ksamil,_Albania_-_beach.JPG?width=1200',
    },
    {
      country: 'romania',
      label: 'Zera Romania',
      tagline: 'Land of the Carpathians',
      path: '/romania',
      image: 'https://commons.wikimedia.org/wiki/Special:FilePath/Bran_Castle_TB1.jpg?width=1200',
    },
  ];

  return (
    <div className="min-h-screen bg-gradient-to-b from-[#241710] via-[#1C120B] to-[#0F0905] text-white flex flex-col">

      <div className="text-center pt-16 pb-10 px-6">
        <span className="font-serif text-4xl tracking-[0.4em] font-light">ZERA</span>
        <p className="font-sans text-[11px] tracking-[0.3em] uppercase text-[#C9A961] mt-3">Travel</p>
        <h1 className="font-serif text-3xl md:text-5xl font-light mt-10 mb-3">Choose Your Destination</h1>
        <p className="font-sans text-sm text-white/50 max-w-md mx-auto">
          Two countries, one curated standard of luxury. Select where your journey begins.
        </p>
      </div>

      <div className="flex-1 grid grid-cols-1 md:grid-cols-2 gap-6 max-w-6xl mx-auto w-full px-6 pb-16">
        {options.map((o) => (
          <button
            key={o.country}
            onClick={() => navigate(o.path)}
            className="group relative h-[420px] overflow-hidden rounded-2xl border border-white/10 text-left"
          >
            <img src={o.image} alt={o.label} className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" />
            <div className="absolute inset-0 bg-gradient-to-t from-black/85 via-black/40 to-black/20" />
            <div className="relative h-full flex flex-col justify-end p-8">
              <div className="flex items-center gap-2 mb-2 text-[#C9A961]">
                <MapPin className="w-4 h-4" />
                <span className="font-sans text-[10px] tracking-[0.3em] uppercase">{o.tagline}</span>
              </div>
              <h2 className="font-serif text-3xl md:text-4xl mb-4">{o.label}</h2>
              <span className="inline-flex items-center gap-2 font-sans text-[11px] tracking-[0.25em] uppercase text-white group-hover:text-[#C9A961] transition-colors">
                Enter Site <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
              </span>
            </div>
          </button>
        ))}
      </div>
    </div>
  );
};

/**
 * `/` renders the country chooser. The full sites live at `/albania` and
 * `/romania` (see App.tsx routing) and reuse the same AppLayout, parametrised
 * by country through CountryProvider.
 */
const Index: React.FC = () => <ChooseCountry />;

export { AppLayout };
export default Index;
